/** Toast actions cross webviews with the exact invitation revision and clicked display. */
import { listen, type UnlistenFn } from "@tauri-apps/api/event";
import {
  useNotificationStore,
  type SystemToast,
} from "@/stores/notification-store";
import { safeTauriUnlisten } from "@/desktop/tauri-listener-cleanup";
import { getWindowDisplayId } from "@/desktop/use-window-role";

export type ConnectionToastActionContext = { displayId: number | null };
const ACTION_EVENT_PREFIX = "denker://connection-health-action/";
type ActionListener = { pending: Promise<UnlistenFn>; revision: string | null };
const actionListeners = new Map<string, ActionListener>();
function releaseActionListener(id: string): void {
  const entry = actionListeners.get(id);
  if (!entry) return;
  actionListeners.delete(id);
  void entry.pending.then(safeTauriUnlisten).catch(() => {});
}

type ConnectionToast = Omit<SystemToast, "actionEvent">;

/** A builder keeps the toast's text in the current app language. */
export function showConnectionToast(
  input: ConnectionToast | (() => ConnectionToast),
  onAction?: (
    context?: ConnectionToastActionContext,
  ) => void | false | Promise<void>,
): void {
  const build = typeof input === "function" ? input : () => input;
  const toast = build();
  releaseActionListener(toast.id);
  const store = useNotificationStore.getState();
  if (!onAction || !toast.actionLabel) {
    store.addSystemToast(build);
    return;
  }
  const actionEvent = `${ACTION_EVENT_PREFIX}${toast.id}`;
  const entry: ActionListener = {
    pending: Promise.resolve(() => {}),
    revision: null,
  };
  const current = () =>
    actionListeners.get(toast.id) === entry &&
    useNotificationStore
      .getState()
      .systemToasts.some(
        (item) => item.id === toast.id && item.revision === entry.revision,
      );
  const run = (displayId: number | null) => {
    if (!current()) return false;
    const acknowledge = () => {
      if (actionListeners.get(toast.id) !== entry) return;
      useNotificationStore
        .getState()
        .removeSystemToast(toast.id, entry.revision);
      releaseActionListener(toast.id);
    };
    const result = onAction({ displayId });
    if (result instanceof Promise) return result.then(acknowledge);
    if (result === false) return false;
    acknowledge();
  };
  entry.pending = listen<{ displayId?: unknown; revision?: unknown }>(
    actionEvent,
    ({ payload }) => {
      if (
        payload?.revision === entry.revision &&
        (payload.displayId === null ||
          (Number.isSafeInteger(payload.displayId) &&
            Number(payload.displayId) >= 0))
      )
        void Promise.resolve(run(payload.displayId as number | null)).catch(
          () => {},
        );
    },
  );
  actionListeners.set(toast.id, entry);
  entry.revision = store.addSystemToast(
    () => ({ ...build(), actionEvent }),
    () => {
      const result = run(getWindowDisplayId());
      if (result === false)
        throw new Error("The connection action is no longer available.");
      return result;
    },
  );
}
export function clearConnectionToast(id: string): void {
  releaseActionListener(id);
  useNotificationStore.getState().removeSystemToast(id);
}
export function disposeConnectionToastListeners(): void {
  for (const id of [...actionListeners.keys()]) releaseActionListener(id);
}
